import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const err_message_detail_required =
  "To proceed, please provide the required details (minimum 5 characters)";

const err_message_task_required = "Please select at least one task";

// Health condition checkboxes schema
const healthConditionSchema = z.object({
  fever: z.boolean().default(false),
  poorFeeding: z.boolean().default(false),
  breathingDifficulties: z.boolean().default(false),
  dizzinessFainting: z.boolean().default(false),
  allergicReactions: z.boolean().default(false),
  behavioralConcerns: z.boolean().default(false),
  specialCareNeeds: z.boolean().default(false),
});

// Babysitter tasks checkboxes schema (matching healthConditions pattern)
const babysitterTasksSchema = z.object({
  feeding: z.boolean().default(false),
  diapering: z.boolean().default(false),
  bathing: z.boolean().default(false),
  napBedtime: z.boolean().default(false),
  playEngagement: z.boolean().default(false),
  lightHousehold: z.boolean().default(false),
});

const babySitterSchema = z
  .object({
    saveAs: z.enum(["Parent/Guardian", "Other"]).default("Parent/Guardian"),
    name: z.string().min(1, "Name is required"),
    age: z
      .string()
      .min(1, "Age is required")
      .refine(
        (val) => {
          // Must start with digit(s), optionally have space, then unit
          return /^\d+\s*[a-zA-Z]+/.test(val);
        },
        {
          message:
            "Age must be a number followed by a unit (e.g., '4 weeks', '2 months')",
        },
      ),
    healthConditions: healthConditionSchema,
    healthConditionsDetails: z.string().optional(),
    babysitter_tasks: babysitterTasksSchema,
    routineDetails: z.string().min(1, "This information is required"),
  })
  .superRefine((val, ctx) => {
    // Check if any health condition is selected
    const hasHealthCondition = Object.values(val.healthConditions).some(
      (condition) => condition === true,
    );
    if (
      hasHealthCondition &&
      (!val.healthConditionsDetails ||
        val.healthConditionsDetails.trim().length < 5)
    ) {
      ctx.addIssue({
        path: ["healthConditionsDetails"],
        code: z.ZodIssueCode.custom,
        message: err_message_detail_required,
      });
    }

    // Check if at least one babysitter task is selected
    const hasTaskSelected = Object.values(val.babysitter_tasks).some(
      (task) => task === true,
    );
    if (!hasTaskSelected) {
      ctx.addIssue({
        path: ["babysitter_tasks"],
        code: z.ZodIssueCode.custom,
        message: err_message_task_required,
      });
    }
  });

export type BabySitterFormData = z.input<typeof babySitterSchema>;

export interface BabySitterFormReturn
  extends UseFormReturn<BabySitterFormData> {
  defaultValues: BabySitterFormData;
}

export const initialFormValues: BabySitterFormData = {
  saveAs: "Parent/Guardian",
  name: "",
  age: "",
  healthConditions: {
    fever: false,
    poorFeeding: false,
    breathingDifficulties: false,
    dizzinessFainting: false,
    allergicReactions: false,
    behavioralConcerns: false,
    specialCareNeeds: false,
  },
  healthConditionsDetails: "",
  babysitter_tasks: {
    feeding: false,
    diapering: false,
    bathing: false,
    napBedtime: false,
    playEngagement: false,
    lightHousehold: false,
  },
  routineDetails: "",
};

export const useBabySitterForm = (
  initialValues?: BabySitterFormData,
): BabySitterFormReturn => {
  const defaultValues = initialValues || initialFormValues;
  const form = useForm<BabySitterFormData>({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(babySitterSchema),
  });

  return {
    ...form,
    defaultValues,
  };
};
